import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  IconUserCircle,
  IconClockCheck,
  IconCalendarWeek,
  IconLayoutDashboard,
  IconUsers,
  IconLogout,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconClock,
  IconReport,
  IconCalendarEvent,
  IconSpeakerphone,
  IconBriefcase,
} from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useSidebar } from '../context/SidebarContext';
import { formatLiveTime, todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import { countPendingLeaves } from '../utils/leaves';
import { countUnseenAnnouncements } from '../utils/announcements';
import tuxLogo from '../assets/tux-logo.png';
import ConfirmModal from './ConfirmModal';
import TimeModeToggle from './TimeModeToggle';

const VA_NAV = [
  { to: '/announcements', label: 'Announcements', Icon: IconSpeakerphone },
  { to: '/profile', label: 'Profile', Icon: IconUserCircle },
  { to: '/checkin', label: 'Check In / Out', Icon: IconClockCheck },
  { to: '/team', label: 'Team', Icon: IconUsers },
  { to: '/weekly', label: 'Weekly Summary', Icon: IconCalendarWeek },
  { to: '/leaves', label: 'Leaves', Icon: IconCalendarEvent },
];

// HQ (Employees/Clients/Holidays, internally tabbed) lives only in
// ADMIN_NAV — unlike Announcements (VA-visible, read-only), none of those
// three have a VA-facing view yet.
const ADMIN_NAV = [
  { to: '/announcements', label: 'Announcements', Icon: IconSpeakerphone },
  { to: '/hq', label: 'HQ', Icon: IconBriefcase },
  { to: '/admin', label: 'Dashboard', Icon: IconLayoutDashboard },
  { to: '/reports', label: 'Reports', Icon: IconReport },
  { to: '/profile', label: 'Profile', Icon: IconUserCircle },
  { to: '/checkin', label: 'Check In / Out', Icon: IconClockCheck },
  { to: '/weekly', label: 'Weekly Summary', Icon: IconCalendarWeek },
  { to: '/leaves', label: 'Leaves', Icon: IconCalendarEvent },
];

const TEAM_ZONES = ['CST', 'AEST', 'PHT'];

function useTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
}

function TeamTimeList({ ownTimezone }) {
  useTick();
  return (
    <div className="flex flex-col gap-1.5">
      {TEAM_ZONES.map((zone) => {
        const { time, date } = formatLiveTime(zone);
        const isOwn = zone === ownTimezone;
        return (
          <div
            key={zone}
            className={`flex items-baseline justify-between ${isOwn ? 'opacity-100' : 'opacity-55'}`}
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{zone}</span>
            <div className="flex flex-col items-end leading-tight">
              <span className="text-xs font-semibold text-ink tabular-nums">{time}</span>
              <span className="text-[10px] text-muted">{date}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamClock({ ownTimezone, collapsed, onOpenPopup }) {
  if (collapsed) {
    return (
      <div className="px-3 py-3 border-t border-border flex justify-center">
        <button
          onClick={onOpenPopup}
          title="Team time"
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-bronze bg-card hover:bg-gold hover:text-white hover:border-gold transition-colors"
        >
          <IconClock size={18} stroke={1.8} />
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-t border-border">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">Team time</p>
        <TimeModeToggle />
      </div>
      <TeamTimeList ownTimezone={ownTimezone} />
    </div>
  );
}

export default function Sidebar() {
  const { currentUser, leaves, announcements, signOut } = useApp();
  const { collapsed, toggleCollapsed, isMobile } = useSidebar();
  const navigate = useNavigate();
  const [timePopupOpen, setTimePopupOpen] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  if (!currentUser) return null;

  const navItems = currentUser.isAdmin ? ADMIN_NAV : VA_NAV;
  // Ambient "you have applications waiting" signal — visible from every
  // page, not just once they happen to open Leaves. Admin-only, since only
  // admins act on pending requests.
  const pendingLeaveCount = currentUser.isAdmin ? countPendingLeaves(leaves) : 0;
  // Unlike the Leaves badge, this one's for everyone — an announcement is
  // relevant to the whole team, not just admins. Recomputed fresh from
  // localStorage on every mount (this component remounts on route changes,
  // same as the rest of Layout), so it clears itself right after a visit to
  // /announcements without any extra plumbing.
  const unseenAnnouncementCount = countUnseenAnnouncements(announcements, todayDateStringInTz(DAY_ANCHOR_TZ));
  const badgeCounts = { '/leaves': pendingLeaveCount, '/announcements': unseenAnnouncementCount };

  const handleSignOut = () => {
    setSignOutConfirmOpen(false);
    signOut();
    navigate('/login');
  };

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-56'} flex-shrink-0 bg-cream border-r border-border flex flex-col h-full sticky top-0 transition-all duration-200`}
    >
      <div className={`flex items-center gap-2.5 py-5 ${collapsed ? 'px-3 justify-center' : 'px-4'}`}>
        <img src={tuxLogo} alt="TUX" className="w-11 h-11 rounded-lg flex-shrink-0" />
        {!collapsed && <span className="text-lg font-semibold text-ink truncate">TrackWithTux</span>}
      </div>

      {!collapsed && (
        <div className="px-4 pb-4 border-b border-border">
          <p className="text-sm font-medium text-ink truncate">{currentUser.name}</p>
          <p className="text-xs text-muted">{currentUser.role}</p>
        </div>
      )}

      <nav className="flex-1 px-3 py-3 flex flex-col gap-1">
        {navItems.map((item) => {
          const badgeCount = badgeCounts[item.to] || 0;
          const showBadge = badgeCount > 0;
          const badgeNoun = item.to === '/leaves' ? 'pending' : 'unread';
          return (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? `${item.label}${showBadge ? ` (${badgeCount} ${badgeNoun})` : ''}` : undefined}
              className={({ isActive }) =>
                `relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  collapsed ? 'justify-center' : ''
                } ${isActive ? 'bg-gold text-white' : 'text-ink hover:bg-card-tint'}`
              }
            >
              <span className="relative flex-shrink-0">
                <item.Icon size={18} stroke={1.8} />
                {showBadge && collapsed && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-error-tx border-2 border-cream" />
                )}
              </span>
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {!collapsed && showBadge && (
                <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-bold bg-error-tx text-white">
                  {badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <TeamClock
        ownTimezone={currentUser.timezone}
        collapsed={collapsed}
        onOpenPopup={() => setTimePopupOpen(true)}
      />

      <div
        className={`px-3 py-3 border-t border-border flex gap-1 ${
          collapsed ? 'flex-col items-center' : 'items-center'
        }`}
      >
        <button
          onClick={() => setSignOutConfirmOpen(true)}
          title={collapsed ? 'Sign out' : undefined}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-bronze hover:bg-card-tint transition-colors min-w-0 ${
            collapsed ? '' : 'flex-1'
          }`}
        >
          <IconLogout size={18} stroke={1.8} className="flex-shrink-0" />
          {!collapsed && <span className="truncate">Sign out</span>}
        </button>
        {!isMobile && (
          <button
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-lg border border-border text-bronze bg-card hover:bg-gold hover:text-white hover:border-gold transition-colors"
          >
            {collapsed ? (
              <IconLayoutSidebarLeftExpand size={18} stroke={1.8} />
            ) : (
              <IconLayoutSidebarLeftCollapse size={18} stroke={1.8} />
            )}
          </button>
        )}
      </div>

      <ConfirmModal
        open={timePopupOpen}
        title="Team time"
        onCancel={() => setTimePopupOpen(false)}
        footer={
          <button
            onClick={() => setTimePopupOpen(false)}
            className="w-full py-2.5 rounded-xl font-medium border border-border text-ink hover:bg-card-tint transition-colors"
          >
            Close
          </button>
        }
      >
        <div className="flex justify-center mb-3">
          <TimeModeToggle />
        </div>
        <TeamTimeList ownTimezone={currentUser.timezone} />
      </ConfirmModal>

      <ConfirmModal
        open={signOutConfirmOpen}
        title="Sign out?"
        confirmLabel="Sign out"
        onConfirm={handleSignOut}
        onCancel={() => setSignOutConfirmOpen(false)}
      >
        <p>You'll need to sign in again to check in/out or view the team.</p>
      </ConfirmModal>
    </aside>
  );
}
